<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportMetricCard from './ReportMetricCard.vue';
import ReportsAPI from 'dashboard/api/reports';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const conversationCount = ref('0');
const conversationCountRaw = ref(0);
const messageCount = ref('0');
const resolutionRate = ref('0');
const handoffRate = ref('0');
const resolvedCount = ref(0);
const handoffCount = ref(0);

const formatToPercent = value => {
  return value ? `${value}%` : '--';
};

const hasChartData = computed(() => conversationCountRaw.value > 0);

const otherCount = computed(() =>
  Math.max(
    conversationCountRaw.value - resolvedCount.value - handoffCount.value,
    0
  )
);

const chartCollection = computed(() => ({
  labels: [
    t('BOT_REPORTS.CHART.RESOLVED'),
    t('BOT_REPORTS.CHART.HANDOFF'),
    t('BOT_REPORTS.CHART.OTHER'),
  ],
  datasets: [
    {
      data: [resolvedCount.value, handoffCount.value, otherCount.value],
      backgroundColor: [
        'rgb(107, 201, 103)',
        'rgb(255, 196, 66)',
        'rgb(199, 199, 199)',
      ],
    },
  ],
}));

const fetchMetrics = () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }
  ReportsAPI.getBotMetrics(props.filters).then(response => {
    conversationCountRaw.value = response.data.conversation_count;
    conversationCount.value = response.data.conversation_count.toLocaleString();
    messageCount.value = response.data.message_count.toLocaleString();
    resolutionRate.value = response.data.resolution_rate.toString();
    handoffRate.value = response.data.handoff_rate.toString();
    resolvedCount.value = response.data.resolved_count;
    handoffCount.value = response.data.handoff_count;
  });
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);
</script>

<template>
  <div
    class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')"
      :value="conversationCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
      :value="messageCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP')"
      :value="formatToPercent(resolutionRate)"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP')"
      :value="formatToPercent(handoffRate)"
      class="flex-1"
    />
    <div v-if="hasChartData" class="w-full h-32 sm:w-48 sm:h-auto">
      <DoughnutChart :collection="chartCollection" />
    </div>
  </div>
</template>
